import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Pencil, Trash2, Package, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductDialog, type ProductData } from "@/components/products/ProductDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ProductTab = "active" | "inactive" | "deleted";

export default function Products() {
  const [activeTab, setActiveTab] = useState<ProductTab>("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductData | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch products based on active tab
  const { data: products, isLoading } = useQuery({
    queryKey: ["products", activeTab, searchQuery, categoryFilter],
    queryFn: async () => {
      let query = supabase
        .from("products")
        .select("*")
        .order("name");

      // Filter by tab
      if (activeTab === "active") {
        query = query.is("deleted_at", null).eq("is_active", true);
      } else if (activeTab === "inactive") {
        query = query.is("deleted_at", null).eq("is_active", false);
      } else if (activeTab === "deleted") {
        query = query.not("deleted_at", "is", null);
      }

      if (searchQuery) {
        query = query.or(
          `name.ilike.%${searchQuery}%,sku.ilike.%${searchQuery}%,category.ilike.%${searchQuery}%`
        );
      }

      if (categoryFilter && categoryFilter !== "all") {
        query = query.eq("category", categoryFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  // Fetch counts for each tab
  const { data: tabCounts } = useQuery({
    queryKey: ["product-tab-counts"],
    queryFn: async () => {
      const [activeResult, inactiveResult, deletedResult] = await Promise.all([
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .is("deleted_at", null)
          .eq("is_active", true),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .is("deleted_at", null)
          .eq("is_active", false),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .not("deleted_at", "is", null),
      ]);

      return {
        active: activeResult.count || 0,
        inactive: inactiveResult.count || 0,
        deleted: deletedResult.count || 0,
      };
    },
  });

  const { data: categories } = useQuery({
    queryKey: ["product-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("category")
        .is("deleted_at", null)
        .not("category", "is", null);
      
      if (error) throw error;
      
      const uniqueCategories = [...new Set(data.map(p => p.category))]
        .filter(c => c && c.trim() !== '')
        .sort();
      return uniqueCategories;
    },
  });

  // Fetch which products are packages (have components) with count
  const { data: packageComponentCounts } = useQuery({
    queryKey: ["package-component-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_components")
        .select("parent_product_id");
      
      if (error) throw error;
      
      // Count components per parent product
      const counts: Record<string, number> = {};
      data.forEach(c => {
        counts[c.parent_product_id] = (counts[c.parent_product_id] || 0) + 1;
      });
      return counts;
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("products")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product-tab-counts"] });
      toast.success("Tuotteen tila päivitetty");
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product-tab-counts"] });
      toast.success("Tuote poistettu");
      setDeletingProductId(null);
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
      setDeletingProductId(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products")
        .update({ deleted_at: null, is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product-tab-counts"] });
      toast.success("Tuote palautettu (ei-aktiiviseksi)");
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  const handleEdit = (product: ProductData) => {
    setEditingProduct(product);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
  };

  const renderProductTable = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Kuva</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>Nimi</TableHead>
            <TableHead>Kategoria</TableHead>
            <TableHead>Yksikkö</TableHead>
            <TableHead className="text-right">Yksikköhinta</TableHead>
            <TableHead className="text-right">ALV %</TableHead>
            {activeTab !== "deleted" && <TableHead>Tila</TableHead>}
            <TableHead className="text-right">Toiminnot</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={activeTab !== "deleted" ? 9 : 8} className="text-center">
                Ladataan...
              </TableCell>
            </TableRow>
          ) : products?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={activeTab !== "deleted" ? 9 : 8} className="text-center">
                {activeTab === "active" && "Ei aktiivisia tuotteita"}
                {activeTab === "inactive" && "Ei ei-aktiivisia tuotteita"}
                {activeTab === "deleted" && "Ei poistettuja tuotteita"}
              </TableCell>
            </TableRow>
          ) : (
            products?.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="h-12 w-12 rounded object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded bg-muted flex items-center justify-center text-muted-foreground text-xs">
                      Ei kuvaa
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {product.sku || "-"}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {product.name}
                    {packageComponentCounts?.[product.id] && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="text-xs gap-1 cursor-help">
                              <Package className="h-3 w-3" />
                              Paketti
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            Sisältää {packageComponentCounts[product.id]} komponenttia
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell>{product.category || "-"}</TableCell>
                <TableCell>{product.unit || "-"}</TableCell>
                <TableCell className="text-right">
                  {Number(product.price).toFixed(2)} €
                </TableCell>
                <TableCell className="text-right">
                  {Number(product.vat_rate).toFixed(2)} %
                </TableCell>
                {activeTab !== "deleted" && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={product.is_active}
                        onCheckedChange={(checked) =>
                          toggleActiveMutation.mutate({
                            id: product.id,
                            is_active: checked,
                          })
                        }
                      />
                      <Badge variant={product.is_active ? "default" : "secondary"}>
                        {product.is_active ? "Aktiivinen" : "Ei aktiivinen"}
                      </Badge>
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex justify-end gap-2">
                    {activeTab === "deleted" ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => restoreMutation.mutate(product.id)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Palauta tuote</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(product)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingProductId(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Tuotteet</h1>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Luo uusi tuote
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ProductTab)}>
          <TabsList>
            <TabsTrigger value="active" className="gap-2">
              Aktiiviset
              <Badge variant="secondary" className="ml-1">
                {tabCounts?.active ?? 0}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="inactive" className="gap-2">
              Ei-aktiiviset
              <Badge variant="secondary" className="ml-1">
                {tabCounts?.inactive ?? 0}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="deleted" className="gap-2">
              Poistetut
              <Badge variant="secondary" className="ml-1">
                {tabCounts?.deleted ?? 0}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Hae tuotteita (nimi, SKU, kategoria)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Kaikki kategoriat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kaikki kategoriat</SelectItem>
                {categories?.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <TabsContent value="active" className="mt-4">
            {renderProductTable()}
          </TabsContent>
          <TabsContent value="inactive" className="mt-4">
            {renderProductTable()}
          </TabsContent>
          <TabsContent value="deleted" className="mt-4">
            {renderProductTable()}
          </TabsContent>
        </Tabs>
      </div>

      <ProductDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        product={editingProduct}
      />

      <AlertDialog
        open={!!deletingProductId}
        onOpenChange={() => setDeletingProductId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vahvista poisto</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa tämän tuotteen? Tämä toiminto ei poista
              tuotetta pysyvästi, mutta se piilotetaan listasta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingProductId && deleteMutation.mutate(deletingProductId)
              }
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
